/**
 * client-session-recovery
 *
 * Browser-side helper for distinguishing a real sign-out from a
 * transient auth drift between the client and server session state.
 *
 * Primary responsibilities:
 * - Re-read the browser Supabase session after a signed-out event
 * - Confirm whether server auth cookies are still valid
 *
 * Dependencies:
 * - lib/supabase/browser-client
 */

import type { Session } from '@supabase/supabase-js'

import {
  DELIBERATE_LOGOUT_EVENT,
  hasDeliberateLogoutIntent
} from '@/lib/auth/client-auth-state'
import { getFreshBrowserSession } from '@/lib/auth/fresh-browser-session'
import { hydrateDesktopHostedBrowserSession } from '@/lib/desktop-browser-session'
import { isDesktopHost } from '@/lib/desktop-host'
import { loadDesktopSessionSnapshot } from '@/lib/desktop-session-access'
import { supabase } from '@/lib/supabase/browser-client'

const SESSION_REFRESH_BUFFER_SECONDS = 5 * 60
const REALTIME_AUTH_SYNC_DEDUPE_WINDOW_MS = 5_000
// Generous timeouts so session recovery completes on slow connections instead
// of being abandoned mid-flight. A timeout here fails open (returns
// serverAuthorized:false; no listener signs the user out), so longer is safe
// and prevents spurious "logged out" states for users on slow internet.
const SESSION_RECOVERY_TIMEOUT_MS = 10_000
const SERVER_AUTH_CHECK_TIMEOUT_MS = 8_000
const SERVER_SESSION_SYNC_TIMEOUT_MS = 10_000
const DESKTOP_AUTH_SNAPSHOT_TIMEOUT_MS = 750

export const AUTH_RECOVERY_FAILED_EVENT = 'losi:auth:recovery-failed'

let lastRealtimeAuthSync: {
  accessToken: string | null
  syncedAt: number
} = {
  accessToken: null,
  syncedAt: 0
}

export interface ClientSessionRecoveryResult {
  session: Session | null
  serverAuthorized: boolean
  timedOut?: boolean
}

export interface AuthRecoveryFailedEventDetail {
  forceRefresh: boolean
  hasBrowserSession: boolean
  serverAuthorized: boolean
}

interface RecoverClientSessionOptions {
  forceRefresh?: boolean
}

let recoveryInFlight: Promise<ClientSessionRecoveryResult> | null = null
let activeRecoveryAbortController: AbortController | null = null
let logoutAbortListenerRegistered = false

const shouldRefreshClientSession = (
  session: Session | null,
  forceRefresh = false
) => {
  if (forceRefresh) {
    return true
  }

  if (!session?.user) {
    return true
  }

  // Unknown expiry cannot prove the token is safe after idle.
  if (typeof session.expires_at !== 'number') {
    return true
  }

  const nowInSeconds = Math.floor(Date.now() / 1000)

  return session.expires_at - nowInSeconds <= SESSION_REFRESH_BUFFER_SECONDS
}

const refreshClientSession = async () => {
  try {
    const { data, error } = await supabase.auth.refreshSession()

    if (error) {
      return null
    }

    return data.session ?? null
  } catch {
    return null
  }
}

const hydrateDesktopSessionForRecovery = async (signal?: AbortSignal) => {
  if (
    typeof window === 'undefined' ||
    !isDesktopHost() ||
    hasDeliberateLogoutIntent() ||
    signal?.aborted
  ) {
    return null
  }

  try {
    const desktopSession = await loadDesktopSessionSnapshot({
      includeTokens: true,
      timeoutMs: DESKTOP_AUTH_SNAPSHOT_TIMEOUT_MS
    })

    if (
      !desktopSession?.isAuthenticated ||
      desktopSession.error ||
      !desktopSession.sessionData?.accessToken ||
      !desktopSession.sessionData?.refreshToken ||
      hasDeliberateLogoutIntent() ||
      signal?.aborted
    ) {
      return null
    }

    return await hydrateDesktopHostedBrowserSession(desktopSession)
  } catch {
    return null
  }
}

const timeoutAfter = <T,>(
  ms: number,
  fallback: T,
  onTimeout?: () => void
) =>
  new Promise<T>((resolve) => {
    window.setTimeout(() => {
      onTimeout?.()
      resolve(fallback)
    }, ms)
  })

const registerLogoutAbortListener = () => {
  if (logoutAbortListenerRegistered || typeof window === 'undefined') {
    return
  }

  logoutAbortListenerRegistered = true
  window.addEventListener(DELIBERATE_LOGOUT_EVENT, () => {
    activeRecoveryAbortController?.abort()
  })
}

const fetchServerAuthStatus = async () => {
  const controller = new AbortController()
  const timeout = window.setTimeout(
    () => controller.abort(),
    SERVER_AUTH_CHECK_TIMEOUT_MS
  )

  try {
    return await fetch('/api/auth', {
      method: 'GET',
      headers: {
        'x-losi-auth-recovery': '1'
      },
      credentials: 'include',
      cache: 'no-store',
      signal: controller.signal
    })
  } finally {
    window.clearTimeout(timeout)
  }
}

const isRecoveryAbortError = (error: unknown) =>
  error instanceof DOMException && error.name === 'AbortError'

const dispatchAuthRecoveryFailed = (
  result: ClientSessionRecoveryResult,
  options: RecoverClientSessionOptions
) => {
  if (
    typeof window === 'undefined' ||
    hasDeliberateLogoutIntent() ||
    result.serverAuthorized ||
    result.timedOut ||
    result.session?.user ||
    !options.forceRefresh
  ) {
    return
  }

  window.dispatchEvent(
    new CustomEvent<AuthRecoveryFailedEventDetail>(
      AUTH_RECOVERY_FAILED_EVENT,
      {
        detail: {
          forceRefresh: !!options.forceRefresh,
          hasBrowserSession: !!result.session?.user,
          serverAuthorized: result.serverAuthorized
        }
      }
    )
  )
}

const syncServerAuthSession = async (
  session: Session | null,
  signal?: AbortSignal
) => {
  const accessToken = session?.access_token?.trim()
  const refreshToken = session?.refresh_token?.trim()
  const expectedUserId = session?.user?.id?.trim()

  if (
    !accessToken ||
    !refreshToken ||
    !expectedUserId ||
    hasDeliberateLogoutIntent() ||
    signal?.aborted
  ) {
    return false
  }

  const controller = new AbortController()
  const abortFromParent = () => controller.abort()
  signal?.addEventListener('abort', abortFromParent, { once: true })
  const timeout = window.setTimeout(
    () => controller.abort(),
    SERVER_SESSION_SYNC_TIMEOUT_MS
  )

  try {
    const response = await fetch('/api/auth/session-sync', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-losi-auth-recovery': '1'
      },
      credentials: 'include',
      cache: 'no-store',
      signal: controller.signal,
      body: JSON.stringify({
        accessToken,
        refreshToken,
        expectedUserId
      })
    })

    if (!response.ok || hasDeliberateLogoutIntent()) {
      return false
    }

    // session-sync may rotate refresh server-side — adopt the verified pair
    // so the browser does not keep a stale refresh that GoTrue already used.
    try {
      const payload = (await response.json()) as {
        accessToken?: unknown
        refreshToken?: unknown
      }
      const syncedAccess =
        typeof payload.accessToken === 'string' ? payload.accessToken.trim() : ''
      const syncedRefresh =
        typeof payload.refreshToken === 'string' ? payload.refreshToken.trim() : ''
      if (
        syncedAccess &&
        syncedRefresh &&
        (syncedAccess !== accessToken || syncedRefresh !== refreshToken)
      ) {
        await supabase.auth.setSession({
          access_token: syncedAccess,
          refresh_token: syncedRefresh
        })
      }
    } catch {
      // Cookie write already succeeded; local adopt is best-effort.
    }

    return !hasDeliberateLogoutIntent()
  } catch (error) {
    if (!isRecoveryAbortError(error)) {
      return false
    }

    return false
  } finally {
    signal?.removeEventListener('abort', abortFromParent)
    window.clearTimeout(timeout)
  }
}

const resolveBrowserSession = async (
  session: Session,
  options: {
    syncServerSession: boolean
    signal?: AbortSignal
  }
) => {
  if (hasDeliberateLogoutIntent() || options.signal?.aborted) {
    return {
      session: null,
      serverAuthorized: false
    }
  }

  const freshSession = await getFreshBrowserSession(supabase, { session })

  if (!freshSession?.user) {
    return {
      session: null,
      serverAuthorized: false
    }
  }

  await syncBrowserRealtimeAuth(freshSession)

  if (hasDeliberateLogoutIntent() || options.signal?.aborted) {
    return {
      session: null,
      serverAuthorized: false
    }
  }

  return {
    session: freshSession,
    serverAuthorized: options.syncServerSession
      ? await syncServerAuthSession(freshSession, options.signal)
      : true
  }
}

export async function syncBrowserRealtimeAuth(
  session: Pick<Session, 'access_token' | 'expires_at'> | null | undefined
) {
  const accessToken = session?.access_token?.trim()

  if (!accessToken) {
    lastRealtimeAuthSync = {
      accessToken: null,
      syncedAt: 0
    }
    return false
  }

  const now = Date.now()

  if (
    lastRealtimeAuthSync.accessToken === accessToken &&
    now - lastRealtimeAuthSync.syncedAt < REALTIME_AUTH_SYNC_DEDUPE_WINDOW_MS
  ) {
    return true
  }

  try {
    // Auth-state callbacks run inside Supabase's subscriber notification loop.
    // Refreshing here emits another auth event and can recurse through that loop.
    await supabase.realtime.setAuth(accessToken)
    lastRealtimeAuthSync = {
      accessToken,
      syncedAt: Date.now()
    }
    return true
  } catch {
    return false
  }
}

export async function syncBrowserSessionToServer(
  session: Session | null | undefined
): Promise<ClientSessionRecoveryResult> {
  if (!session?.user) {
    return {
      session: null,
      serverAuthorized: false
    }
  }

  return resolveBrowserSession(session, {
    syncServerSession: true
  })
}

async function recoverClientSessionOnce(
  { forceRefresh = false }: RecoverClientSessionOptions = {},
  signal?: AbortSignal
): Promise<ClientSessionRecoveryResult> {
  if (hasDeliberateLogoutIntent()) {
    return {
      session: null,
      serverAuthorized: false
    }
  }

  await hydrateDesktopSessionForRecovery(signal)

  if (signal?.aborted || hasDeliberateLogoutIntent()) {
    return {
      session: null,
      serverAuthorized: false
    }
  }

  const initialSession = (await supabase.auth.getSession()).data.session

  if (signal?.aborted || hasDeliberateLogoutIntent()) {
    return {
      session: null,
      serverAuthorized: false
    }
  }

  if (
    initialSession?.user &&
    !shouldRefreshClientSession(initialSession, forceRefresh)
  ) {
    return resolveBrowserSession(initialSession, {
      syncServerSession: true,
      signal
    })
  }

  const refreshedSession = await refreshClientSession()

  if (signal?.aborted || hasDeliberateLogoutIntent()) {
    return {
      session: null,
      serverAuthorized: false
    }
  }

  if (refreshedSession?.user) {
    return resolveBrowserSession(refreshedSession, {
      syncServerSession: true,
      signal
    })
  }

  const currentSession = (await supabase.auth.getSession()).data.session

  if (signal?.aborted || hasDeliberateLogoutIntent()) {
    return {
      session: null,
      serverAuthorized: false
    }
  }

  if (currentSession?.user) {
    return resolveBrowserSession(currentSession, {
      syncServerSession: true,
      signal
    })
  }

  try {
    const response = await fetchServerAuthStatus()

    if (!response.ok) {
      return {
        session: null,
        serverAuthorized: false
      }
    }
  } catch {
    return {
      session: null,
      serverAuthorized: false
    }
  }

  const recoveredSession = (await supabase.auth.getSession()).data.session

  if (!recoveredSession?.user) {
    return {
      session: null,
      serverAuthorized: true
    }
  }

  return resolveBrowserSession(recoveredSession, {
    syncServerSession: false,
    signal
  })
}

export async function recoverClientSession(
  options: RecoverClientSessionOptions = {}
): Promise<ClientSessionRecoveryResult> {
  registerLogoutAbortListener()

  if (recoveryInFlight) {
    return recoveryInFlight
  }

  const abortController = new AbortController()
  activeRecoveryAbortController = abortController

  const recovery = Promise.race([
    recoverClientSessionOnce(options, abortController.signal),
    timeoutAfter<ClientSessionRecoveryResult>(
      SESSION_RECOVERY_TIMEOUT_MS,
      {
        session: null,
        serverAuthorized: false,
        timedOut: true
      },
      () => abortController.abort()
    )
  ])
    .then((result) => {
      dispatchAuthRecoveryFailed(result, options)
      return result
    })
    .finally(() => {
      if (recoveryInFlight === recovery) {
        recoveryInFlight = null
      }
      if (activeRecoveryAbortController === abortController) {
        activeRecoveryAbortController = null
      }
    })

  recoveryInFlight = recovery
  return recovery
}
